// La vitrina del onboarding en desktop (ADR-2026-10-02-onboarding-v2-vitrina-desktop): imágenes
// creadas con Atlas (con la foto de la que salieron) y lo que dicen las marcas. Desde
// ADR-2026-10-05-onboarding-v2-vitrina-en-una-columna van juntas, una arriba de la otra, en una
// columna al costado del recuadro del paso (que ocupa el 70%). De a una, cada 6 s, con un fundido;
// las dos van desfasadas para no cambiar a la vez. Las reviews se pausan con el mouse encima (son
// para leer); las imágenes no.

import { useEffect, useRef, useState } from 'react';
import {
  IMAGENES_DE_VITRINA,
  iniciales,
  reviewsDeLaVitrina,
  type ImagenDeVitrina,
  type ReviewDeVitrina,
} from '../../../lib/onboardingMarca/vitrina';
import './vitrina.css';

const MS_POR_TARJETA = 6_000;
const DESFASE_MS = 3_000;

function pideMenosMovimiento(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * Rota un índice cada `ms`, salvo pausado. Devuelve el actual y el anterior (para el fundido).
 * `lista(i)`: si la tarjeta `i` ya se puede mostrar (su imagen bajó); si no, se espera a la
 * próxima vuelta en vez de pasar a una tarjeta vacía.
 */
function useRotacion(
  total: number,
  { ms, desde, pausado, lista }: { ms: number; desde: number; pausado: boolean; lista?: (i: number) => boolean },
) {
  const [paso, setPaso] = useState<{ actual: number; previo: number | null }>({ actual: 0, previo: null });
  const listaRef = useRef(lista);
  listaRef.current = lista;
  useEffect(() => {
    if (total < 2 || pausado) return;
    let intervalo: number | undefined;
    const avanzar = () =>
      setPaso((p) => {
        const siguiente = (p.actual + 1) % total;
        if (listaRef.current && !listaRef.current(siguiente)) return p;
        return { actual: siguiente, previo: p.actual };
      });
    const arranque = window.setTimeout(() => {
      avanzar();
      intervalo = window.setInterval(avanzar, ms);
    }, desde);
    return () => {
      window.clearTimeout(arranque);
      window.clearInterval(intervalo);
    };
  }, [total, ms, desde, pausado]);
  return paso;
}

/** Baja las imágenes de antemano y cuenta cuáles ya están, para no pasar a una que no cargó. */
function usePrecarga(srcs: string[]): (src: string) => boolean {
  const cargadas = useRef(new Set<string>());
  useEffect(() => {
    for (const s of srcs) {
      const img = new Image();
      img.onload = () => cargadas.current.add(s);
      img.src = s;
      if (img.complete) cargadas.current.add(s);
    }
  }, [srcs.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps
  return (src) => cargadas.current.has(src);
}

function TarjetaDeImagen({ item, estado }: { item: ImagenDeVitrina; estado: 'entra' | 'sale' }) {
  return (
    <figure className={`om-vitrina__tarjeta om-vitrina__imagen is-${estado}`}>
      <img src={item.src} alt={`${item.tipo} creada con Atlas (${item.rubro})`} className="om-vitrina__foto" />
      <span className="om-vitrina__tipo">
        <span className="material-symbols-rounded" aria-hidden="true">
          auto_awesome
        </span>
        {item.tipo}
      </span>
      <span className="om-vitrina__antes">
        <img src={item.antes} alt="La foto original" />
        <span>Antes</span>
      </span>
    </figure>
  );
}

function TarjetaDeReview({ item, estado }: { item: ReviewDeVitrina; estado: 'entra' | 'sale' }) {
  return (
    <figure className={`om-vitrina__tarjeta om-vitrina__review is-${estado}`}>
      <span className="om-vitrina__estrellas" aria-label="5 de 5">
        {'★★★★★'}
      </span>
      <blockquote className="om-vitrina__cita">“{item.texto}”</blockquote>
      <figcaption className="om-vitrina__quien">
        <span className="om-vitrina__avatar" aria-hidden="true">
          {iniciales(item.nombre)}
        </span>
        <span className="min-w-0">
          <span className="block truncate font-extrabold text-brand-black">{item.nombre}</span>
          <span className="block truncate text-[#6B7398]">{item.negocio}</span>
        </span>
      </figcaption>
    </figure>
  );
}

function Puntos({ total, actual }: { total: number; actual: number }) {
  return (
    <span className="om-vitrina__puntos" aria-hidden="true">
      {Array.from({ length: total }, (_, i) => (
        <span key={i} className={i === actual ? 'is-actual' : ''} />
      ))}
    </span>
  );
}

export function Vitrina() {
  const [quieta] = useState(pideMenosMovimiento);
  const [pausaReviews, setPausaReviews] = useState(false);
  const reviews = reviewsDeLaVitrina();
  const conReviews = reviews.length > 0;

  const cargada = usePrecarga(IMAGENES_DE_VITRINA.flatMap((x) => [x.src, x.antes]));
  // Las imágenes no se pausan con el mouse (no hay nada que leer); las reviews sí.
  const imagenes = useRotacion(IMAGENES_DE_VITRINA.length, {
    ms: MS_POR_TARJETA,
    desde: MS_POR_TARJETA,
    pausado: false,
    lista: (i) => cargada(IMAGENES_DE_VITRINA[i].src),
  });
  const opiniones = useRotacion(reviews.length, {
    ms: MS_POR_TARJETA,
    desde: MS_POR_TARJETA + DESFASE_MS,
    pausado: pausaReviews,
  });

  // Con menos movimiento no hay fundido: el anterior no se dibuja.
  const previoImagen = quieta ? null : imagenes.previo;
  const previoReview = quieta ? null : opiniones.previo;

  // Una sola columna al costado del recuadro: las imágenes arriba y lo que dicen abajo
  // (ADR-2026-10-05-onboarding-v2-vitrina-en-una-columna). Sin reviews (producción, mientras
  // sólo haya ejemplos), las imágenes solas y más grandes.
  return (
    <aside className={`om-vitrina ${conReviews ? '' : 'om-vitrina--solo-imagenes'}`} aria-label="Creado con Atlas">
      <section className="om-vitrina__bloque" aria-label="Imágenes creadas con Atlas">
        <p className="om-vitrina__titulo">Creado con Atlas</p>
        <div className="om-vitrina__escena om-vitrina__escena--imagen">
          {previoImagen !== null && (
            <TarjetaDeImagen
              key={`p-${previoImagen}-${imagenes.actual}`}
              item={IMAGENES_DE_VITRINA[previoImagen]}
              estado="sale"
            />
          )}
          <TarjetaDeImagen key={`a-${imagenes.actual}`} item={IMAGENES_DE_VITRINA[imagenes.actual]} estado="entra" />
        </div>
        <Puntos total={IMAGENES_DE_VITRINA.length} actual={imagenes.actual} />
      </section>

      {conReviews && (
        <section
          className="om-vitrina__bloque"
          aria-label="Lo que dicen de Atlas"
          onMouseEnter={() => setPausaReviews(true)}
          onMouseLeave={() => setPausaReviews(false)}
        >
          <p className="om-vitrina__titulo">Lo que dicen de Atlas</p>
          <div className="om-vitrina__escena om-vitrina__escena--review">
            {previoReview !== null && (
              <TarjetaDeReview key={`p-${previoReview}-${opiniones.actual}`} item={reviews[previoReview]} estado="sale" />
            )}
            <TarjetaDeReview key={`a-${opiniones.actual}`} item={reviews[opiniones.actual]} estado="entra" />
          </div>
          <Puntos total={reviews.length} actual={opiniones.actual} />
        </section>
      )}
    </aside>
  );
}

export default Vitrina;
